import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CheckCircle2, XCircle, Clock, ShieldCheck, AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { describeStage, useApprovalRulesMap } from '../lib/approvalBracket';

/**
 * Approve / Reject panel embedded in the Requisition + PO doc-stack drawers.
 *
 * Renders three things in one card:
 *   1. Chain progress (Requisition only) — visual list of stages, who acted,
 *      who's next. Surfaces the sequential constraint clearly.
 *   2. Eligibility note — "you're up", "waiting on X first", or "you're
 *      not on this approval chain".
 *   3. Approve / Reject buttons + optional note, gated on the user being
 *      the CURRENT-stage approver.
 *
 * On success we invalidate the doc query, the dashboard's pending-approvals
 * widget, and the issue-notes pending-list so every surface that watches
 * this doc re-fetches without a manual refresh. The drawer stays open so
 * the user can verify the new status before closing.
 *
 * The server enforces the same gate (services/approval-chain.ts +
 * pos.routes.ts approver-email check) — this UI is a clarity layer, not
 * the security boundary.
 */

interface ChainEntry {
  stageIndex: number;
  ruleName: string;
  approverEmails: string[];
  status: 'pending' | 'approved' | 'rejected' | 'skipped';
  by?: { name?: string; email?: string } | string | null;
  byEmail?: string;
  at?: string | null;
  note?: string;
}

interface CommonProps {
  docId: string;
  // Doc status — used to decide whether to render anything at all.
  status: string;
  /** Mutation-result side effects: refetch dashboard + doc, leave drawer
   *  open so the approver sees the new state. */
  onApproved?: () => void;
}

interface RequisitionProps extends CommonProps {
  kind: 'requisition';
  approvals?: ChainEntry[];
  /** Top-level approvalEmails (current stage). Used when approvals[] isn't
   *  populated (legacy reqs pre-chain). */
  approvalEmails?: string[];
}

interface POProps extends CommonProps {
  kind: 'po';
}

type Props = RequisitionProps | POProps;

const isOpenStatus = (s: string) => {
  const v = String(s || '').toLowerCase();
  return v === 'submitted' || v === 'pending_approval';
};

export function ApprovalActionPanel(props: Props) {
  if (!isOpenStatus(props.status)) return null;
  if (props.kind === 'requisition') return <RequisitionPanel {...props} />;
  return <POPanel {...props} />;
}

function RequisitionPanel({ docId, approvals, approvalEmails, onApproved }: RequisitionProps) {
  const { user } = useAuth();
  const myEmail = (user?.email || '').toLowerCase();
  const chain = approvals ?? [];

  const currentIdx = useMemo(() => chain.findIndex((s) => s.status === 'pending'), [chain]);
  const currentStage = currentIdx >= 0 ? chain[currentIdx] : null;
  const isCurrentApprover = currentStage
    ? currentStage.approverEmails.map((e) => e.toLowerCase()).includes(myEmail)
    : // No chain on the doc but a top-level approvalEmails list — accept
      // that as the gate. Covers legacy reqs that never went through the
      // sequential cascade.
      (approvalEmails || []).map((e) => e.toLowerCase()).includes(myEmail);

  const onChainButNotCurrent = !isCurrentApprover && chain.some((s) =>
    s.status === 'pending' && s.approverEmails.map((e) => e.toLowerCase()).includes(myEmail),
  );
  const isAdmin = user?.role === 'admin';

  return (
    <ActionCard
      title="Approval"
      chain={chain}
      myEmail={myEmail}
      currentIdx={currentIdx}
    >
      {isCurrentApprover ? (
        <DecisionButtons
          endpoint={`/api/requisitions/${encodeURIComponent(docId)}/approve`}
          invalidateKeys={[
            ['my-approvals'],
            ['doc-requisition', docId],
            ['issue-notes'],
            ['requisitions'],
            ['requisitions-list'],
          ]}
          onAfterSuccess={onApproved}
        />
      ) : onChainButNotCurrent ? (
        <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <Clock size={14} />
          You're on this approval chain but not the current stage.
          Waiting on{' '}
          <span className="font-medium">{currentStage?.ruleName || 'previous stage'}</span>
          {currentStage?.approverEmails?.length ? ` (${currentStage.approverEmails.join(', ')})` : ''} first.
        </div>
      ) : isAdmin ? (
        <DecisionButtons
          adminOverride
          endpoint={`/api/requisitions/${encodeURIComponent(docId)}/approve`}
          invalidateKeys={[
            ['my-approvals'],
            ['doc-requisition', docId],
            ['issue-notes'],
            ['requisitions'],
            ['requisitions-list'],
          ]}
          onAfterSuccess={onApproved}
        />
      ) : (
        <div className="text-xs text-slate-500 italic">You're not an approver on this requisition.</div>
      )}
    </ActionCard>
  );
}

function POPanel({ docId, onApproved }: POProps) {
  const { data, isLoading } = useQuery<{
    canApprove: boolean;
    status: string;
    ruleName: string;
    approverEmails: string[];
    isAdminOverride?: boolean;
    blockedBy: string[];
    reason?: string;
  }>({
    queryKey: ['po-approval-context', docId],
    queryFn: () => api.get(`/api/pos/${encodeURIComponent(docId)}/approval-context`),
    staleTime: 30_000,
  });
  if (isLoading || !data) return null;

  return (
    <div className="card border-amber-200 bg-amber-50/30 p-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
        <ShieldCheck size={14} /> Approval
        {data.ruleName && (
          <span className="ml-2 text-xs font-normal text-amber-800">
            rule: {data.ruleName}
          </span>
        )}
      </div>
      {data.approverEmails?.length > 0 && (
        <div className="text-[11px] text-slate-600">
          Approvers on this rule: <span className="font-mono">{data.approverEmails.join(', ')}</span>
        </div>
      )}
      {data.blockedBy?.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-100 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle size={14} />
          {data.reason || `Waiting on ${data.blockedBy.join(' + ')} stage on the indent first.`}
        </div>
      )}
      {data.canApprove ? (
        <DecisionButtons
          adminOverride={data.isAdminOverride}
          endpoint={`/api/pos/${encodeURIComponent(docId)}/approve`}
          invalidateKeys={[
            ['my-approvals'],
            ['doc-po', docId],
            ['po-approval-context', docId],
            ['pos'],
            ['po-pending-reminders'],
          ]}
          onAfterSuccess={onApproved}
        />
      ) : !data.blockedBy?.length ? (
        <div className="text-xs text-slate-500 italic">{data.reason || "You're not an approver on this PO."}</div>
      ) : null}
    </div>
  );
}

function ActionCard({
  title, chain, myEmail, currentIdx, children,
}: {
  title: string;
  chain: ChainEntry[];
  myEmail: string;
  currentIdx: number;
  children: React.ReactNode;
}) {
  const rulesById = useApprovalRulesMap();
  return (
    <div className="card border-amber-200 bg-amber-50/30 p-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
        <ShieldCheck size={14} /> {title}
      </div>
      {chain.length > 0 && (
        <ol className="space-y-1.5">
          {chain.map((s, i) => {
            const isMe = s.approverEmails.map((e) => e.toLowerCase()).includes(myEmail);
            return (
              <li key={i} className={`flex items-center gap-2 text-sm ${i === currentIdx ? 'font-semibold' : ''}`}>
                {s.status === 'approved' && <CheckCircle2 size={14} className="text-emerald-600 flex-none" />}
                {s.status === 'rejected' && <XCircle size={14} className="text-rose-600 flex-none" />}
                {s.status === 'skipped' && <CheckCircle2 size={14} className="text-slate-400 flex-none" />}
                {s.status === 'pending' && (
                  <Clock size={14} className={i === currentIdx ? 'text-amber-700 flex-none' : 'text-slate-400 flex-none'} />
                )}
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  i === currentIdx ? 'bg-amber-200 text-amber-900' : 'bg-slate-100 text-slate-600'
                }`}>
                  Stage {s.stageIndex + 1}
                </span>
                <span className="truncate">{describeStage(s as any, rulesById).full}</span>
                <span className="text-[11px] text-slate-500 truncate">
                  {s.approverEmails.join(', ')}
                </span>
                {isMe && i === currentIdx && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-50 text-brand-700 font-semibold">YOU</span>
                )}
                {s.status === 'approved' && s.at && (
                  <span className="text-[10px] text-emerald-700 ml-auto">
                    by {s.byEmail || (typeof s.by === 'object' ? s.by?.name : s.by) || '—'}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {children}
    </div>
  );
}

function DecisionButtons({
  endpoint, invalidateKeys, onAfterSuccess, adminOverride,
}: {
  endpoint: string;
  invalidateKeys: Array<readonly unknown[]>;
  onAfterSuccess?: () => void;
  adminOverride?: boolean;
}) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const submit = useMutation({
    mutationFn: (approved: boolean) => api.post(endpoint, { approved, note: note.trim() || undefined }),
    onSuccess: (_d, approved) => {
      toast.success(approved ? 'Approved' : 'Rejected');
      for (const key of invalidateKeys) {
        qc.invalidateQueries({ queryKey: key as any });
      }
      onAfterSuccess?.();
    },
    onError: (e: any) => toast.error(e?.message || 'Action failed'),
  });

  return (
    <div className="space-y-2">
      {adminOverride && (
        <div className="text-[11px] text-purple-700 bg-purple-50 border border-purple-200 rounded px-2 py-1">
          Admin override — you can act on this even though it's not on your approval chain.
        </div>
      )}
      <textarea
        className="input text-sm min-h-[60px]"
        placeholder="Optional note (visible on the approval log + email trail)…"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary bg-emerald-600 hover:bg-emerald-700 flex-1"
          disabled={submit.isPending}
          onClick={() => submit.mutate(true)}
        >
          <CheckCircle2 size={14} /> {submit.isPending ? 'Saving…' : 'Approve'}
        </button>
        <button
          type="button"
          className="btn-primary bg-rose-600 hover:bg-rose-700 flex-1"
          disabled={submit.isPending}
          onClick={() => {
            if (!note.trim()) {
              if (!confirm('Reject without a reason? It helps the requester to know why.')) return;
            }
            submit.mutate(false);
          }}
        >
          <XCircle size={14} /> {submit.isPending ? 'Saving…' : 'Reject'}
        </button>
      </div>
    </div>
  );
}
